import { lazy, Suspense, type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader.js';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { useSbSelector } from '../../reducers/useSbSelector.ts';
// components
import { SharedFollowTopButton } from '../../shared/SharedFollowTopButton/SharedFollowTopButton.tsx';
import { SbRespFollowSearchBannerLazy } from '../SbRespFollowSearchBanner/SbRespFollowSearchBannerLazy.tsx';
import { SharedBottomModule } from '../../shared/SharedBottomModule/SharedBottomModule.tsx';
import { SbSharedTitle } from '../SbSharedTitle/SbSharedTitle.tsx';
import { SbSharedPageBreadcrumbs } from '../SbSharedPageBreadcrumbs/SbSharedPageBreadcrumbs.tsx';
import { SbSharedTopTileController } from '../SbSharedTopTileController/SbSharedTopTileController.jsx';
import { SbRespCategoryTiles } from '../SbRespCategoryTiles/SbRespCategoryTiles.tsx';
import { SbRespDesignTiles } from '../SbRespDesignTiles/SbRespDesignTiles.tsx';
import { SbSharedBillboardAdWrapper } from '../SbSharedBillboardAd/SbSharedBillboardAd.tsx';
import { SbRespNullSearchResults } from '../SbRespNullSearchResults/SbRespNullSearchResults.jsx';
import { SbRespInteriorsWordpressArticlesWrapper } from '../SbRespInteriorsModule/SbRespInteriorsWordpressArticlesWrapper.tsx';
import { SharedRelatedPages } from '../../shared/SharedRelatedPages/SharedRelatedPages.jsx';
import { SbSharedRelatedCreators } from '../SbSharedRelatedCreators/SbSharedRelatedCreators.tsx';
import { SharedRespLinksBlock } from '../../shared/SharedRespLinksBlock/SharedRespLinksBlock.tsx';
import { SbRespNotificationUnavailableItem } from '../SbRespNotifications/SbRespNotificationUnavailableItem/SbRespNotificationUnavailableItem.jsx';
import { SbRespPriceTiles } from '../SbRespPriceTiles/SbRespPriceTiles.tsx';
import { TradePromoLoader } from '../../global/TradePromo/TradePromoLoader.jsx';
import { SbRespTradeOnApprovalLoadingBanner } from '../SbRespTradeOnApprovalPromo/SbRespTradeOnApprovalPromoBanner/SbRespTradeOnApprovalLoadingBanner.jsx';
import { SbSharedPersonalizedFilterToast } from '../SbSharedPersonalizedFilterToast/SbSharedPersonalizedFilterToast.jsx';
import { SbRespMainContent } from '../SbRespMainContent/SbRespMainContent.tsx';
import { SbSharedMarketTrends } from '../SbSharedMarketTrends/SbSharedMarketTrends.tsx';
import { SbSharedForceSearchLink } from '../SbSharedForceSearchLink/SbSharedForceSearchLink.tsx';
import { SbRespContentModuleSelector } from '../SbRespContentModuleSelector/SbRespContentModuleSelector.tsx';
import { SbSharedControlledHeadingWrapper } from '../SbSharedControlledHeadingWrapper/SbSharedControlledHeadingWrapper.tsx';
import { SbSharedRelatedSearches } from '../SbSharedRelatedSearches/SbSharedRelatedSearches.tsx';
import { SbRespL1Landing } from '../SbRespL1Landing/SbRespL1Landing.tsx';
import { SharedFAQ } from '../../shared/SharedFAQ/SharedFAQ.tsx';
import { RecentlyViewedItemsLazy } from 'dibs-recent-history/exports/RecentlyViewedItemsLazy.ts';
import { SbSharedRecommendedItems } from '../SbSharedRecommendedItems/SbSharedRecommendedItems.tsx';
import { getFilterValueByUrlLabel } from '../SbSharedRefineMenu/sbSharedRefineMenuHelpers.ts';
import { SharedSaveSearchContextProvider } from '../../shared/SharedSaveSearchContext/SharedSaveSearchContext.tsx';
import { useCurrency } from 'dibs-buyer-layout/exports/useCurrency.tsx';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';

// constants
import { pageTypeConstants as pageTypes } from '../../constants/pageTypeConstants.ts';
import { SEARCH_TYPES, FILTER_SEARCH_TYPE } from '../../constants/sbConstants.ts';
import {
    LANDING_MODULE_TYPE,
    STORYTELLING_MODULE_TYPE,
} from '../../constants/contentModuleConstants.js';
import { FILTER_REFINE_SEARCH } from '../SbSharedRefineMenu/sbSharedRefineMenuConstants.ts';
import {
    MIN_THIN_PAGE_COUNT,
    REVEAL_RELATED_CREATORS_COUNT_DESKTOP,
} from '../../constants/sbConstants.ts';
const { SEARCH, NEW_ARRIVALS } = pageTypes;

// styles
import dibsCss from 'dibs-css';
import styles from './SbRespComponents.scss';
import classnames from 'classnames';
import { resolveBrowseContentModuleBreadcrumbsTone } from '../SbSharedPageBreadcrumbs/sbBrowseContentModuleBreadcrumbsTone.ts';

import { type SbRespComponents_viewer$key } from './__generated__/SbRespComponents_viewer.graphql.ts';
import {
    type SbRespComponents_itemSearch$key,
    type SbRespComponents_itemSearch$data,
} from './__generated__/SbRespComponents_itemSearch.graphql.ts';
import { type SbRespComponents_user$key } from './__generated__/SbRespComponents_user.graphql.ts';
import { type SbRespComponents_shippingFilters$key } from './__generated__/SbRespComponents_shippingFilters.graphql.ts';

const SbRespTradeOnApprovalPromoBannerLazy = lazy(
    () =>
        import(
            /* webpackChunkName: "TradeOnApprovalPromo" */ '../SbRespTradeOnApprovalPromo/SbRespTradeOnApprovalPromoBanner/SbRespTradeOnApprovalPromoBannerLazy.jsx'
        )
);

const viewerFragment = graphql`
    fragment SbRespComponents_viewer on Viewer {
        isRelatedCreatorsModuleEnabled: featureFlag(feature: "relatedCreatorsModule")
        ...SharedRelatedPages_viewer
        ...SharedRespLinksBlock_viewer
        ...SbRespNullSearchResults_viewer
        ...SbRespPriceTiles_viewer @include(if: $isTrade)
        ...SbRespMainContent_viewer
        ...SbRespTradeOnApprovalPromoBanner_viewer @include(if: $isTrade)
        ...SbSharedTopTileController_viewer
        ...SbRespInteriorsWordpressArticlesWrapper_viewer
        ...SbSharedRecommendedItems_viewer
        ...SharedRecentSales_viewer
        ...SbSharedMarketTrends_viewer
        ...SbRespContentModuleSelector_viewer
    }
`;
const itemSearchFragment = graphql`
    fragment SbRespComponents_itemSearch on ItemSearchQueryConnection {
        totalResults
        pageType
        isL1LandingPage
        bottomModule {
            ...SharedBottomModule_bottomModule
        }
        ...SbSharedControlledHeadingWrapper_itemSearch
        ...SbSharedForceSearchLink_itemSearch
        ...SbSharedSearchSuggestions_itemSearch
        ...SbRespNullSearchResults_itemSearch
        ...SbSharedPageBreadcrumbs_itemSearch
        ...SbSharedTitle_itemSearch
        ...SbSharedTopTileController_itemSearch
        ...SbSharedRelatedSearches_itemSearch
        ...SbRespCategoryTiles_itemSearch
        ...SbRespDesignTiles_itemSearch
        ...SharedSaveSearchContext_itemSearch
        ...SbSharedBillboardAd_itemSearch
        ...SbRespPriceTiles_itemSearch @include(if: $isTrade)
        ...SbRespTradeOnApprovalPromoBanner_itemSearch @include(if: $isTrade)
        ...SbRespMainContent_itemSearch
        browseContentModule(contentModuleId: $contentModuleId) {
            moduleType
            ...sbBrowseContentModuleBreadcrumbsTone_browseContentModule
        }
        ...SbRespContentModuleSelector_itemSearch
        ...SbRespInteriorsWordpressArticlesWrapper_itemSearch
        ...SharedWordpressArticles_itemSearch
        ...SharedFollowTopButton_itemSearch
        ...SbRespL1Landing_itemSearch
        appliedFilters {
            name
            values {
                urlLabel
            }
        }
        ...SharedRelatedPages_itemSearch
        ...SbSharedRelatedCreators_itemSearch
        ...SharedRespLinksBlock_itemSearch
        internalBuyLinks {
            ...SharedRespLinksBlock_links
        }
        ...SharedFAQ_itemSearch
        ...SharedRecentSales_itemSearch
        soldItems: soldResults(first: 28) {
            totalResults
        }
        ...SharedYMAL_itemSearch
        ...SbRespFollowSearchBanner_itemSearch
        ...SbSharedMarketTrends_itemSearch
    }
`;
const userFragment = graphql`
    fragment SbRespComponents_user on User {
        ...TradePromo_user @include(if: $isTrade)
        ...SbRespNullSearchResults_user
        ...SbRespFollowSearchBanner_user
        ...SharedFollowTopButton_user
        ...SbSharedTitle_user
        ...SbSharedPageBreadcrumbs_user
        ...SharedSaveSearchContext_user
        ...SbRespMainContent_user
        ...SbRespInteriorsWordpressArticlesWrapper_user
        ...SharedRecentSales_user
        ...SharedYMAL_user
        ...SbRespContentModuleSelector_user
    }
`;
const shippingFiltersFragment = graphql`
    fragment SbRespComponents_shippingFilters on ShippingFilterType @relay(plural: true) {
        ...SbSharedPersonalizedFilterToast_shippingFilters
    }
`;

type AppliedFilter = NonNullable<SbRespComponents_itemSearch$data['appliedFilters']>;

function getSearchTerm(filters: AppliedFilter | null, isSearchPage: boolean): string {
    const searchKey = isSearchPage ? SEARCH : FILTER_REFINE_SEARCH;
    return filters?.find(filter => filter?.name === searchKey)?.values?.[0]?.urlLabel || '';
}

type Props = {
    viewer: SbRespComponents_viewer$key;
    itemSearch: SbRespComponents_itemSearch$key;
    user: SbRespComponents_user$key | null;
    shippingFilters: SbRespComponents_shippingFilters$key | null;
    isClient: boolean;
};

export const SbRespComponents: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
    user: userRef,
    shippingFilters: shippingFiltersRef,
    isClient,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const user = useFragment(userFragment, userRef);
    const shippingFilters = useFragment(shippingFiltersFragment, shippingFiltersRef);
    const { totalResults, appliedFilters, pageType, isL1LandingPage, soldItems } = itemSearch;

    const bottomModule = itemSearch.bottomModule?.filter(filterNulls);
    const internalBuyLinks = itemSearch.internalBuyLinks?.filter(filterNulls) || [];

    const { isRelatedCreatorsModuleEnabled } = viewer;
    const generatedUriRef = useSbSelector(state => state.filters.generatedUriRef);
    const relayVariables = useSbSelector(state => state.relayVariables.variables);

    const isSearch = pageType === SEARCH;
    const showNullSearchResultPage = !totalResults;
    const isThinPage = !!totalResults && totalResults < MIN_THIN_PAGE_COUNT;
    const searchTerm = getSearchTerm(appliedFilters, isSearch);
    const isFuzzyResults = !!getFilterValueByUrlLabel(
        appliedFilters,
        FILTER_SEARCH_TYPE,
        SEARCH_TYPES.FUZZY
    );

    const { currency } = useCurrency();
    const { isBot, isQuickFiltersRedesignVariant, isDriveSavedSearchAdoptionVariant } =
        useServerVarsContext();
    const browseContentModule = itemSearch?.browseContentModule;
    const isLandingContentModule = browseContentModule?.moduleType === LANDING_MODULE_TYPE;
    const isStorytellingContentModule =
        browseContentModule?.moduleType === STORYTELLING_MODULE_TYPE;
    const isContentModuleWithOwnHeader = isLandingContentModule || isStorytellingContentModule;
    const breadcrumbsToneClass = resolveBrowseContentModuleBreadcrumbsTone(browseContentModule);

    //if has 0 results or it's fuzzy results with sold items (SEARCH-1598)
    if (showNullSearchResultPage || (isFuzzyResults && soldItems?.totalResults)) {
        return (
            <SharedSaveSearchContextProvider
                itemSearch={itemSearch}
                user={user}
                searchTerm={searchTerm}
                uriRef={generatedUriRef}
            >
                <SbRespNullSearchResults
                    viewer={viewer}
                    itemSearch={itemSearch}
                    user={user}
                    isClient={isClient}
                    searchTerm={searchTerm}
                />
            </SharedSaveSearchContextProvider>
        );
    }

    // Currently search and recognized dealers pages have their saveSearch cta above main items container
    // Buy and designs pages have their saveSearchButton above main items container too, but these pages uses their own SbRespComponents
    const showTopSaveSearchButton =
        isQuickFiltersRedesignVariant ||
        !(pageType === pageTypes.SEARCH || pageType === pageTypes.RECOGNIZED_DEALER);

    return (
        <SharedSaveSearchContextProvider
            itemSearch={itemSearch}
            user={user}
            searchTerm={searchTerm}
            uriRef={generatedUriRef}
        >
            {/* Breadcrumbs */}
            {!isSearch &&
                (isContentModuleWithOwnHeader ? (
                    <div className={classnames(dibsCss.relative, breadcrumbsToneClass)}>
                        <SbSharedPageBreadcrumbs itemSearch={itemSearch} user={user} />
                    </div>
                ) : (
                    <SbSharedPageBreadcrumbs itemSearch={itemSearch} user={user} />
                ))}
            <SbSharedControlledHeadingWrapper itemSearch={itemSearch}>
                {Heading => (
                    <>
                        {isL1LandingPage ? (
                            /* L1 Page Header */
                            <SbRespL1Landing itemSearch={itemSearch} />
                        ) : (
                            /* Page Header */

                            <div className={classnames(dibsCss.flex, dibsCss.flexCol)}>
                                {!isContentModuleWithOwnHeader && (
                                    <div
                                        className={classnames(
                                            dibsCss.flex,
                                            dibsCss.flexCol,
                                            dibsCss.justifyBetween,
                                            {
                                                [dibsCss.mbMedium]: !isQuickFiltersRedesignVariant,
                                                [dibsCss.mbXsmall]: isQuickFiltersRedesignVariant,
                                            }
                                        )}
                                    >
                                        <div
                                            className={classnames(dibsCss.flex, {
                                                [dibsCss.itemsCenter]:
                                                    !isQuickFiltersRedesignVariant,
                                                [dibsCss.itemsBaseline]:
                                                    isQuickFiltersRedesignVariant,
                                                [dibsCss.justifyBetween]:
                                                    isQuickFiltersRedesignVariant &&
                                                    !isDriveSavedSearchAdoptionVariant,
                                                [dibsCss.gapXsmall]:
                                                    isQuickFiltersRedesignVariant &&
                                                    isDriveSavedSearchAdoptionVariant,
                                            })}
                                        >
                                            <SbSharedTitle
                                                itemSearch={itemSearch}
                                                user={user}
                                                Heading={Heading}
                                            >
                                                {showTopSaveSearchButton &&
                                                    !isQuickFiltersRedesignVariant && (
                                                        <SharedFollowTopButton
                                                            itemSearch={itemSearch}
                                                            user={user}
                                                            addLeadingSpace // isQuickFiltersRedesignVariant abTest is successful, this prop is not needed anymore
                                                        />
                                                    )}
                                            </SbSharedTitle>
                                            {isQuickFiltersRedesignVariant && (
                                                <SharedFollowTopButton
                                                    itemSearch={itemSearch}
                                                    user={user}
                                                />
                                            )}
                                        </div>
                                    </div>
                                )}
                                {relayVariables.isTrade && isClient && (
                                    <div className={classnames(dibsCss.grow, dibsCss.textRight)}>
                                        <TradePromoLoader
                                            showIcon
                                            user={user}
                                            displayInfo={null}
                                            item={null}
                                        />
                                    </div>
                                )}
                                {/* Related Searches */}
                                <SbSharedRelatedSearches itemSearch={itemSearch} />
                            </div>
                        )}
                        {/* Content Module */}
                        <SbRespContentModuleSelector
                            itemSearch={itemSearch}
                            user={user}
                            viewer={viewer}
                        />
                        <SbSharedForceSearchLink itemSearch={itemSearch} />
                        {/* Broad Search Category Tiles, or, for trade users, Price Range Tiles or On-Approval banner)*/}
                        <SbSharedTopTileController
                            itemSearch={itemSearch}
                            categoryTiles={<SbRespCategoryTiles itemSearch={itemSearch} />}
                            designTiles={<SbRespDesignTiles itemSearch={itemSearch} />}
                            tradeOnApprovalBanner={
                                /* FINDING-13641 Workaround until React Suspense supports server-side
                                 * rendering > https://reactjs.org/docs/code-splitting.html#reactlazy
                                 */
                                isClient ? (
                                    <Suspense fallback={<SbRespTradeOnApprovalLoadingBanner />}>
                                        <SbRespTradeOnApprovalPromoBannerLazy
                                            itemSearch={itemSearch}
                                            viewer={viewer}
                                        />
                                    </Suspense>
                                ) : (
                                    <SbRespTradeOnApprovalLoadingBanner />
                                )
                            }
                            priceTiles={
                                <SbRespPriceTiles itemSearch={itemSearch} viewer={viewer} />
                            }
                            viewer={viewer}
                        />
                        <SbRespMainContent
                            currency={currency}
                            itemSearch={itemSearch}
                            viewer={viewer}
                            user={user}
                            isClient={isClient}
                            isThinPage={isThinPage}
                            hideSaveSearchButton={showTopSaveSearchButton}
                        />
                        {/* Ad */}
                        {!isThinPage && (
                            <SbSharedBillboardAdWrapper
                                itemSearch={itemSearch}
                                className={classnames(dibsCss.mbXlarge, dibsCss.mtMedium)}
                            />
                        )}
                        {/* Creator Market Trends */}
                        <SbSharedMarketTrends viewer={viewer} itemSearch={itemSearch} />
                        {/* Related Searches With Title */}
                        <SbSharedRelatedSearches itemSearch={itemSearch} showTitle />
                        {/* Follow Search */}
                        {isClient && (
                            <SbRespFollowSearchBannerLazy itemSearch={itemSearch} user={user} />
                        )}
                        {/* Bottom Module */}
                        <SharedBottomModule
                            className={styles.bottomModule}
                            bottomModule={bottomModule}
                        />
                        {/* Related Creators for creator pages */}
                        {isRelatedCreatorsModuleEnabled && (
                            <SbSharedRelatedCreators
                                itemSearch={itemSearch}
                                revealCreatorCount={REVEAL_RELATED_CREATORS_COUNT_DESKTOP}
                            />
                        )}
                        {/* Questions & Answers */}
                        <SharedFAQ itemSearch={itemSearch} />
                        {/* Interiors and/or Editorial */}
                        <SbRespInteriorsWordpressArticlesWrapper
                            itemSearch={itemSearch}
                            viewer={viewer}
                            user={user}
                            isClient={isClient}
                        />
                        {isSearch && isThinPage && (
                            <div
                                className={classnames(
                                    dibsCss.flex,
                                    dibsCss.flexCol,
                                    dibsCss.wFull,
                                    dibsCss.minW0,
                                    dibsCss.pySmall,
                                    dibsCss.px0
                                )}
                            >
                                {/* Recommended Items */}
                                <SbSharedRecommendedItems viewer={viewer} />
                            </div>
                        )}
                        {/* Recently Viewed Items */}
                        <RecentlyViewedItemsLazy
                            authModalLoader={authModalLoader}
                            hasTopSeparator={pageType === NEW_ARRIVALS}
                            isBot={isBot}
                        />
                        {/*  Related Pages */}
                        <SharedRelatedPages
                            viewer={viewer}
                            itemSearch={itemSearch}
                            LinksBlockComponent={SharedRespLinksBlock}
                            borderTop={false}
                        />
                        {/*  More Ways to Browse */}
                        <SharedRespLinksBlock
                            viewer={viewer}
                            item={null}
                            itemSearch={itemSearch}
                            links={internalBuyLinks}
                        />
                    </>
                )}
            </SbSharedControlledHeadingWrapper>

            {/* Unavailable Item Modal */}
            <SbRespNotificationUnavailableItem />
            {/* Personalized Filter Toast */}
            <SbSharedPersonalizedFilterToast shippingFilters={shippingFilters} />
        </SharedSaveSearchContextProvider>
    );
};
